import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import {diApi} from '@di-shared/api/di';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {DemeritEducationReservationFormType} from '@di-shared/features/public-service/demerit-education/DemeritEducationReservationForm';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {useSuspenseQuery} from '@tanstack/react-query';

interface DemeritEducationReservationStep4CenterProps {
  form: DemeritEducationReservationFormType;
  onUpdateForm(partial: Partial<DemeritEducationReservationFormType>): void;
  onNext(): void;
}

function DemeritEducationReservationStep4Center(props: DemeritEducationReservationStep4CenterProps) {
  const {t} = useInferableTranslation();
  const {backOrClose, openAlert} = useCcWebviewUtilsHelper();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [handleError] = usePendingHandler(async () => {
    await openAlert({
      title: t('T10314'),
      message: t('T10328'),
      okText: t('T10221'),
    });
    backOrClose();
  });

  return (
    <ErrorBoundary
      fallbackRender={() => null}
      onError={handleError}
    >
      <Suspense
        fallback={
          <div className="wrap">
            <Header left={<HeaderBackButton />}>
              <h1 className="headline_xxsmall nav_center_title">{t('T10319')}</h1>
            </Header>
            <main className="content">
              <section className="section section_inspection">
                <div className="inspection_info">
                  <p className="place_text">
                    <span className="ic ic_basic_location_16px" />
                    <span className={clsx('region', {is_select: props.form.branchName})}>{props.form.branchName || t('T10272')}</span>
                    <span className={clsx('location', {is_select: props.form.centerName})}>{props.form.centerName || t('T10261')}</span>
                  </p>
                  <p
                    className={clsx('inspection_text', {
                      is_select:
                        (props.form.educationStartDate && props.form.educationEndDate) ||
                        (props.form.educationStartTime && props.form.educationEndTime),
                    })}
                  >
                    <span className="ic ic_calendar_16px" />
                    <span className={clsx('date', {is_select: props.form.educationStartDate && props.form.educationEndDate})}>
                      {props.form.educationStartDate && props.form.educationEndDate
                        ? `${CldrDatetimeFormatter.format(props.form.educationStartDate, 'Md')} - ${CldrDatetimeFormatter.format(
                            props.form.educationEndDate,
                            'Md'
                          )}`
                        : t('T10320')}
                    </span>
                    <span className={clsx('time', {is_select: props.form.educationStartTime && props.form.educationEndTime})}>
                      {props.form.educationStartTime && props.form.educationEndTime
                        ? `${CldrDatetimeFormatter.format(props.form.educationStartTime, 'aHM')} - ${CldrDatetimeFormatter.format(
                            props.form.educationEndTime,
                            'aHM'
                          )}`
                        : t('T10276')}
                    </span>
                  </p>
                </div>
                <ul className="place_list">
                  {[
                    {name: '강남도로교통교육점', centerAddr: '서울시 영등포구 양평로 26길 1 (지하철 1번 출구에서 도보로 약 10~15분)'},
                    {name: '강남도로교통교육점', centerAddr: '서울시 강남구 구성로 313강 (지하철 2번 출구에서 도보로 약 10~15분)'},
                    {name: '강남도로교통교육점', centerAddr: '서울시 강북구 강북로 123길 3 (지하철 3번 출구에서 도보로 약 10~15분)'},
                  ].map((center) => (
                    <li
                      className="place_item"
                      key={center.name}
                    >
                      <div className="place_info">
                        <span className="place_name skeleton">{center.name}</span>
                        <p className="address skeleton">{center.centerAddr}</p>
                      </div>
                      <button
                        type="button"
                        className="btn_choice"
                      >
                        <span className="ic ic_check_12px" />
                        {t('T10321')}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            </main>
            <div className="floating_action_area">
              <div className="button_box_complex">
                <button
                  type="button"
                  className="btn btn_large btn_outline btn_full"
                  onClick={backOrClose}
                >
                  {t('T10316')}
                </button>
                <button
                  type="button"
                  className="btn btn_large btn_filled btn_full"
                  disabled
                >
                  {t('T20348')}
                </button>
              </div>
            </div>
          </div>
        }
      >
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({form, onUpdateForm, onNext}: DemeritEducationReservationStep4CenterProps) {
  const {t} = useInferableTranslation();
  const {backOrClose, openAlert} = useCcWebviewUtilsHelper();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const centerParams = {
    branchCode: form.branchCode,
    eduStartDate: form.educationStartDate ? getYYYYMMddFormat(form.educationStartDate) : '',
    eduEndDate: form.educationEndDate ? getYYYYMMddFormat(form.educationEndDate) : '',
  };
  const {data: centerData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.demeritEducationController.getCenters), centerParams],
    queryFn: async () => {
      // 정상 조회 완료(E00) 이외에는 에러로 취급
      // E00 정상 | C95 연계 오류 | C96 암복호화에러 | C97 파라메터 없음 | C98 필수 파라메터 값 없음 | C99 시스템 오류
      // https://hmg.dooray.com/project/tasks/4117484859229622126

      const response = await diApi.demeritEducationController.getCenters(centerParams);
      if (response.data.resultCode !== 'E00') {
        throw new Error('center data not available');
      }
      // 교육장이 없을 경우 안내 후 이전 화면으로 이동
      if (response.data.contents === null) {
        await openAlert({
          title: t('T10353'),
          okText: t('T10221'),
        });
        backOrClose();
      }
      return response;
    },
    select: ({data}) => data.contents,
  });

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('T10319')}</h1>
      </Header>
      <main className="content">
        <section className="section section_inspection">
          <div className="inspection_info">
            <p className="place_text">
              <span className="ic ic_basic_location_16px" />
              <span className={clsx('region', {is_select: form.branchName})}>{form.branchName || t('T10272')}</span>
              <span className={clsx('location', {is_select: form.centerName})}>{form.centerName || t('T10261')}</span>
            </p>
            <p
              className={clsx('inspection_text', {
                is_select: (form.educationStartDate && form.educationEndDate) || (form.educationStartTime && form.educationEndTime),
              })}
            >
              <span className="ic ic_calendar_16px" />
              <span className={clsx('date', {is_select: form.educationStartDate && form.educationEndDate})}>
                {form.educationStartDate && form.educationEndDate
                  ? `${CldrDatetimeFormatter.format(form.educationStartDate, 'Md')} - ${CldrDatetimeFormatter.format(
                      form.educationEndDate,
                      'Md'
                    )}`
                  : t('T10320')}
              </span>
              <span className={clsx('time', {is_select: form.educationStartTime && form.educationEndTime})}>
                {form.educationStartTime && form.educationEndTime
                  ? `${CldrDatetimeFormatter.format(form.educationStartTime, 'aHM')} - ${CldrDatetimeFormatter.format(
                      form.educationEndTime,
                      'aHM'
                    )}`
                  : t('T10276')}
              </span>
            </p>
          </div>
          <ul className="place_list">
            {centerData?.map((center) => (
              <li
                className="place_item"
                key={center.code}
              >
                <div className="place_info">
                  <span className="place_name">{center.name}</span>
                  <p className="address">{center.centerAddr}</p>
                </div>
                <button
                  type="button"
                  className={clsx('btn_choice', {is_active: center.code === form.centerCode})}
                  onClick={() =>
                    onUpdateForm({
                      centerName: center.name,
                      centerCode: center.code,
                      centerNotice: center.centerNotice,
                      classroomCode: '',
                      educationDate: null,
                      educationStartTime: null,
                      educationEndTime: null,
                    })
                  }
                >
                  <span className="ic ic_check_12px" />
                  {t('T10321')}
                </button>
              </li>
            ))}
          </ul>
        </section>
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
            onClick={backOrClose}
          >
            {t('T10316')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            disabled={!(form.centerCode && form.centerName && form.centerNotice)}
            onClick={onNext}
          >
            {t('T20348')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DemeritEducationReservationStep4Center;
